import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, ArrowUpDown, ArrowUp, ArrowDown, Eye,
  X, Users, GitCompare, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelConfig = {
  focus: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'FOCUS' },
  developing: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', label: 'DEVELOPING' },
  proficient: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'PROFICIENT' },
  unrated: { bg: 'bg-zinc-500/15', text: 'text-zinc-400', border: 'border-zinc-500/30', label: 'UNRATED' }
};

const decisionConfig = {
  'Ready for Promotion': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', short: 'PROMOTION' },
  'Maintain Level': { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', short: 'MAINTAIN' },
  'Needs Development': { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', short: 'DEVELOP' },
  'Performance Drop': { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30', short: 'DROP' },
  'Inconsistency': { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/20', short: 'INCONSIST.' },
  'No Data': { bg: 'bg-zinc-500/10', text: 'text-zinc-500', border: 'border-zinc-500/20', short: 'NO DATA' }
};

const gapConfig = {
  'consistent': { text: 'text-emerald-400', label: 'Consistent' },
  'training_stronger': { text: 'text-orange-400', label: 'Train > Match' },
  'match_stronger': { text: 'text-blue-400', label: 'Match > Train' },
  'no_data': { text: 'text-zinc-500', label: '—' }
};

const ClubIntelligence = () => {
  const { club, filters } = useOutletContext();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('overall_score');
  const [sortDir, setSortDir] = useState('desc');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterPosition, setFilterPosition] = useState('all');
  const [comparing, setComparing] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (club?.id) fetchAthletes();
  }, [club, filters]);

  const fetchAthletes = async () => {
    const token = localStorage.getItem('club_token');
    const params = new URLSearchParams();
    if (filters?.season) params.append('season', filters.season);
    if (filters?.team_id) params.append('team_id', filters.team_id);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    const qs = params.toString();
    try {
      const res = await axios.get(`${API}/clubs/intelligence/athletes${qs ? '?' + qs : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAthletes(res.data);
    } catch (err) {
      console.error('Fetch athletes error:', err);
    }
    setLoading(false);
  };

  // Unique teams, positions
  const teams = useMemo(() => [...new Set(athletes.map(a => a.team_name))], [athletes]);
  const positions = useMemo(() => [...new Set(athletes.map(a => a.position).filter(Boolean))], [athletes]);

  // Filter + Sort
  const filtered = useMemo(() => {
    let result = [...athletes];
    if (search) result = result.filter(a => a.name?.toLowerCase().includes(search.toLowerCase()));
    if (filterTeam !== 'all') result = result.filter(a => a.team_name === filterTeam);
    if (filterLevel !== 'all') result = result.filter(a => a.level === filterLevel);
    if (filterPosition !== 'all') result = result.filter(a => a.position === filterPosition);
    result.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return result;
  }, [athletes, search, filterTeam, filterLevel, filterPosition, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const toggleCompare = (id) => {
    setComparing(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 3 ? [...prev, id] : prev);
  };

  const comparedAthletes = athletes.filter(a => comparing.includes(a.athlete_id));

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={12} className="text-zinc-600" />;
    return sortDir === 'asc' ? <ArrowUp size={12} className="text-primary" /> : <ArrowDown size={12} className="text-primary" />;
  };

  const ScoreCell = ({ value }) => {
    const color = value >= 8.1 ? 'text-emerald-400' : value >= 6.1 ? 'text-amber-400' : value >= 4 ? 'text-red-400' : 'text-zinc-500';
    return <span className={`font-mono text-sm ${color}`}>{value > 0 ? value : '—'}</span>;
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="club-intelligence">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl uppercase tracking-wide">
            Player <span className="text-primary">Intelligence</span>
          </h1>
          <p className="text-muted-foreground text-sm">{filtered.length} athletes · {athletes.filter(a => a.has_evaluation).length} evaluated</p>
        </div>
        {comparing.length >= 2 && (
          <Button onClick={() => setShowCompare(true)} className="bg-primary text-black hover:bg-primary/90" data-testid="compare-btn">
            <GitCompare size={16} className="mr-2" /> Compare ({comparing.length})
          </Button>
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search athletes..."
            className="w-full bg-card border border-border/40 rounded-sm pl-9 pr-3 py-2 text-sm outline-none focus:border-primary/50"
            data-testid="intelligence-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
          className="border-border/40 text-muted-foreground"
        >
          <Filter size={14} className="mr-2" /> Filters
          {(filterTeam !== 'all' || filterLevel !== 'all' || filterPosition !== 'all') && (
            <span className="ml-2 w-5 h-5 bg-primary text-black rounded-full text-xs flex items-center justify-center">
              {[filterTeam, filterLevel, filterPosition].filter(f => f !== 'all').length}
            </span>
          )}
        </Button>
      </div>

      {/* Expandable Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="bg-card border border-border/40 rounded-sm p-4 flex flex-wrap gap-4"
          >
            <FilterSelect label="Team" value={filterTeam} onChange={setFilterTeam} options={teams.map(t => ({ value: t, label: t }))} />
            <FilterSelect label="Level" value={filterLevel} onChange={setFilterLevel}
              options={[
                { value: 'proficient', label: 'Proficient' },
                { value: 'developing', label: 'Developing' },
                { value: 'focus', label: 'Focus' },
                { value: 'unrated', label: 'Unrated' }
              ]}
            />
            <FilterSelect label="Position" value={filterPosition} onChange={setFilterPosition}
              options={positions.map(p => ({ value: p, label: p }))}
            />
            <button onClick={() => { setFilterTeam('all'); setFilterLevel('all'); setFilterPosition('all'); }}
              className="text-xs text-muted-foreground hover:text-primary ml-auto self-end"
            >Clear All</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table */}
      <div className="bg-card border border-border/40 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead>
              <tr className="border-b border-border/30 bg-zinc-900/50">
                <th className="px-3 py-3 text-left w-8">
                  <span className="text-[10px] text-muted-foreground">CMP</span>
                </th>
                <ThSort label="Name" field="name" onClick={handleSort}><SortIcon field="name" /></ThSort>
                <ThSort label="Age" field="age" onClick={handleSort}><SortIcon field="age" /></ThSort>
                <th className="px-3 py-3 text-left"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Position</span></th>
                <th className="px-3 py-3 text-left"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Team</span></th>
                <ThSort label="Overall" field="overall_score" onClick={handleSort}><SortIcon field="overall_score" /></ThSort>
                <ThSort label="Technical" field="technical_score" onClick={handleSort}><SortIcon field="technical_score" /></ThSort>
                <ThSort label="Physical" field="physical_score" onClick={handleSort}><SortIcon field="physical_score" /></ThSort>
                <ThSort label="Tactical" field="tactical_score" onClick={handleSort}><SortIcon field="tactical_score" /></ThSort>
                <th className="px-3 py-3 text-left"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Level</span></th>
                <th className="px-3 py-3 text-left"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Decision</span></th>
                <ThSort label="Goals" field="goals" onClick={handleSort}><SortIcon field="goals" /></ThSort>
                <ThSort label="Assists" field="assists" onClick={handleSort}><SortIcon field="assists" /></ThSort>
                <th className="px-3 py-3 text-left"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Gap</span></th>
                <th className="px-3 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => {
                const lc = levelConfig[a.level] || levelConfig.unrated;
                const dc = decisionConfig[a.decision] || decisionConfig['No Data'];
                return (
                  <motion.tr key={a.athlete_id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className={`border-b border-border/10 hover:bg-zinc-800/30 transition-colors ${comparing.includes(a.athlete_id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={comparing.includes(a.athlete_id)}
                        onChange={() => toggleCompare(a.athlete_id)}
                        className="w-3.5 h-3.5 accent-primary cursor-pointer"
                        data-testid={`compare-check-${a.athlete_id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-primary shrink-0">
                          {a.name?.charAt(0)}
                        </div>
                        <span className="text-sm font-medium truncate max-w-[140px]">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground">{a.age || '—'}</td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground">{a.position || '—'}</td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground truncate max-w-[120px]">{a.team_name}</td>
                    <td className="px-3 py-2.5"><ScoreCell value={a.overall_score} /></td>
                    <td className="px-3 py-2.5"><ScoreCell value={a.technical_score} /></td>
                    <td className="px-3 py-2.5"><ScoreCell value={a.physical_score} /></td>
                    <td className="px-3 py-2.5"><ScoreCell value={a.tactical_score} /></td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${lc.bg} ${lc.text} ${lc.border} border`}>
                        {lc.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${dc.bg} ${dc.text} ${dc.border} border`}>
                        {dc.short || a.decision}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-sm font-mono ${a.goals > 0 ? 'text-primary' : 'text-zinc-600'}`}>{a.goals || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-sm font-mono ${a.assists > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>{a.assists || '—'}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {(() => { const gc = gapConfig[a.gap] || gapConfig['no_data']; return (
                        <span className={`text-[9px] uppercase tracking-wider ${gc.text}`}>{gc.label}</span>
                      );})()}
                    </td>
                    <td className="px-3 py-2.5">
                      <Link to={`/club/${club?.id}/intelligence/player/${a.athlete_id}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                        data-testid={`view-player-${a.athlete_id}`}
                      >
                        <Eye size={15} />
                      </Link>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            {athletes.length === 0 ? 'No athletes in the club yet' : 'No athletes match the current filters'}
          </div>
        )}
      </div>

      {/* Compare Modal */}
      <AnimatePresence>
        {showCompare && comparedAthletes.length >= 2 && (
          <CompareModal athletes={comparedAthletes} onClose={() => setShowCompare(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

const ThSort = ({ label, field, onClick, children }) => (
  <th className="px-3 py-3 text-left cursor-pointer select-none group" onClick={() => onClick(field)}>
    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">
      {label} {children}
    </span>
  </th>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="bg-zinc-900 border border-border/40 rounded-sm px-2 py-1.5 text-sm outline-none focus:border-primary/50 min-w-[120px]"
    >
      <option value="all">All</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const CompareModal = ({ athletes, onClose }) => {
  const skills = ['ball_control', 'passing', 'dribbling', 'finishing', 'speed_agility', 'balance_coordination', 'decision_making', 'game_awareness'];
  const skillLabels = {
    ball_control: 'Ball Control', passing: 'Passing', dribbling: 'Dribbling', finishing: 'Finishing',
    speed_agility: 'Speed & Agility', balance_coordination: 'Balance', decision_making: 'Decision Making', game_awareness: 'Awareness'
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}
    >
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="bg-card border border-border/40 rounded-sm max-w-4xl w-full max-h-[80vh] overflow-auto p-6"
        onClick={e => e.stopPropagation()} data-testid="compare-modal"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl uppercase">Player <span className="text-primary">Comparison</span></h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-white"><X size={20} /></button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/30">
                <th className="py-2 text-left text-xs text-muted-foreground uppercase">Metric</th>
                {athletes.map(a => (
                  <th key={a.athlete_id} className="py-2 text-center text-sm font-medium">{a.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <CompareRow label="Overall" values={athletes.map(a => a.overall_score)} />
              <CompareRow label="Technical" values={athletes.map(a => a.technical_score)} />
              <CompareRow label="Physical" values={athletes.map(a => a.physical_score)} />
              <CompareRow label="Tactical" values={athletes.map(a => a.tactical_score)} />
              {skills.map(s => (
                <CompareRow key={s} label={skillLabels[s] || s}
                  values={athletes.map(a => a.radar_data?.[s] ? parseFloat(a.radar_data[s]) : 0)}
                />
              ))}
              <tr className="border-t border-border/30">
                <td className="py-2 text-xs text-muted-foreground uppercase">Level</td>
                {athletes.map(a => {
                  const lc = levelConfig[a.level] || levelConfig.unrated;
                  return (
                    <td key={a.athlete_id} className="py-2 text-center">
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm ${lc.bg} ${lc.text} ${lc.border} border`}>{lc.label}</span>
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td className="py-2 text-xs text-muted-foreground uppercase">Decision</td>
                {athletes.map(a => {
                  const dc = decisionConfig[a.decision] || decisionConfig['No Data'];
                  return (
                    <td key={a.athlete_id} className="py-2 text-center">
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-sm ${dc.bg} ${dc.text} ${dc.border} border`}>{a.decision}</span>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CompareRow = ({ label, values }) => {
  const max = Math.max(...values.filter(v => v > 0));
  return (
    <tr className="border-b border-border/10">
      <td className="py-2 text-xs text-muted-foreground">{label}</td>
      {values.map((v, i) => {
        const isMax = v === max && v > 0 && values.filter(x => x === max).length === 1;
        const color = v >= 8.1 ? 'text-emerald-400' : v >= 6.1 ? 'text-amber-400' : v >= 4 ? 'text-red-400' : 'text-zinc-500';
        return (
          <td key={i} className={`py-2 text-center font-mono text-sm ${color} ${isMax ? 'font-bold' : ''}`}>
            {v > 0 ? v.toFixed(1) : '—'}
          </td>
        );
      })}
    </tr>
  );
};

export default ClubIntelligence;
